// Call cadence + fee-consent renewal: pure date logic for the book and Today view.
import { addMonthsIso } from './bookEdit.js';
import type { PracticeSettings } from './practiceSettings.js';
import { SEGMENTS, type Segment } from './types.js';

/** Next Call Due after a call made on `today`, by service tier. null when the tier has no cadence. */
export function nextCallDue(segment: string | null | undefined, today: string, settings: PracticeSettings): string | null {
  // Unsegmented (or a legacy tier label): quarterly until it's tiered.
  if (!segment || !(SEGMENTS as readonly string[]).includes(segment)) return addMonthsIso(today, 3);
  const months = settings.callCadenceMonths[segment as Segment];
  return months == null ? null : addMonthsIso(today, months);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export type ConsentStatus = 'none' | 'current' | 'dueSoon' | 'renewalOpen' | 'lapsed';

export const CONSENT_LABEL: Record<ConsentStatus, string> = {
  none: 'No date',
  current: 'Current',
  dueSoon: 'Due soon',
  renewalOpen: 'Renewal open',
  lapsed: 'Lapsed',
};

export interface ConsentInfo {
  status: ConsentStatus;
  /** Date consent falls due (the OFA anniversary). */
  dueDate: string | null;
  /** Last day the client can renew before the arrangement ends. */
  deadline: string | null;
  /** Whole days from today to the deadline (negative once lapsed). */
  daysToDeadline: number | null;
}

/**
 * Fee-consent renewal state. The due date is the earliest OFA due date on the
 * household (Macquarie / Shaws), else 12 months after the last recorded
 * consent. Renewal stays open for `renewalWindowDays` after the due date.
 */
export function consentInfo(
  input: { ofaDates: Array<string | null | undefined>; lastConsent: string | null | undefined },
  today: string,
  settings: PracticeSettings,
): ConsentInfo {
  const ofa = input.ofaDates.filter((d): d is string => !!d && /^\d{4}-\d{2}-\d{2}/.test(d)).map((d) => d.slice(0, 10)).sort();
  const dueDate = ofa[0] ?? (input.lastConsent ? addMonthsIso(input.lastConsent.slice(0, 10), 12) : null);
  if (!dueDate) return { status: 'none', dueDate: null, deadline: null, daysToDeadline: null };
  const deadline = addDaysIso(dueDate, settings.feeConsent.renewalWindowDays);
  const daysToDeadline = Math.round((Date.parse(`${deadline}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  let status: ConsentStatus;
  if (today > deadline) status = 'lapsed';
  else if (today >= dueDate) status = 'renewalOpen';
  else if (today >= addDaysIso(dueDate, -settings.feeConsent.warnDays)) status = 'dueSoon';
  else status = 'current';
  return { status, dueDate, deadline, daysToDeadline };
}
